import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import i18n from '../../../i18n';
import { Trans } from 'react-i18next';
import { fetchUomOptionsByCategory } from '../../../services/productCatalogService';

/**
 * Two-level Unit of Measure tree selector.
 * Level 1: metric categories from GET /product-metrics/categories
 * Level 2: metrics from GET /product-metrics/search/category?category=...
 */
export default function MetricTreeSelect({
  categories = [],
  value = '',
  categoryValue = '',
  displayLabel = '',
  onChange,
  disabled = false,
  error = false,
  loadingCategories = false,
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [childrenByCategory, setChildrenByCategory] = useState({});
  const [loadingCategory, setLoadingCategory] = useState(null);
  const [childError, setChildError] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadedCategoriesRef = useRef({});

  // Prefetch children for the currently selected metric category (edit preload).
  useEffect(() => {
    if (!categoryValue) return undefined;
    setExpanded((prev) => ({ ...prev, [categoryValue]: true }));
    if (loadedCategoriesRef.current[categoryValue]) return undefined;

    let cancelled = false;
    loadedCategoriesRef.current[categoryValue] = true;
    setLoadingCategory(categoryValue);
    fetchUomOptionsByCategory(categoryValue, { size: 100 })
      .then((list) => {
        if (cancelled) return;
        setChildrenByCategory((prev) => ({ ...prev, [categoryValue]: list || [] }));
      })
      .catch((err) => {
        if (!cancelled) {
          loadedCategoriesRef.current[categoryValue] = false;
          setChildError(err?.message || i18n.t('inventory.failedToLoadMetrics'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCategory(null);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryValue]);

  const selectedLabel = (() => {
    if (displayLabel) return displayLabel;
    if (!value) return '';
    for (const list of Object.values(childrenByCategory)) {
      const hit = (list || []).find((m) => String(m.id) === String(value));
      if (hit) return hit.label;
    }
    return value ? i18n.t('inventory.metricNumber', { id: value }) : '';
  })();

  const toggleCategory = async (cat) => {
    const isOpen = !!expanded[cat];
    setExpanded((prev) => ({ ...prev, [cat]: !isOpen }));
    if (isOpen || loadedCategoriesRef.current[cat]) return;

    setLoadingCategory(cat);
    setChildError(null);
    loadedCategoriesRef.current[cat] = true;
    try {
      const list = await fetchUomOptionsByCategory(cat, { size: 100 });
      setChildrenByCategory((prev) => ({ ...prev, [cat]: list || [] }));
    } catch (err) {
      loadedCategoriesRef.current[cat] = false;
      setChildError(err?.message || i18n.t('inventory.failedToLoadMetricsForCategory'));
      setChildrenByCategory((prev) => ({ ...prev, [cat]: [] }));
    } finally {
      setLoadingCategory(null);
    }
  };

  const selectMetric = (cat, metric) => {
    onChange?.({
      metricId: String(metric.id),
      metricCategory: cat,
      label: metric.label,
    });
    setOpen(false);
  };

  return (
    <div className="inv-metric-tree" ref={rootRef}>
      <button
        type="button"
        className={`inv-metric-tree__trigger form-select ${error ? 'form-input-error' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        style={{
          height: '40px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          borderColor: error ? 'var(--accent-red)' : undefined,
          width: '100%',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selectedLabel ? 'var(--text-dark)' : 'var(--text-grey)',
          }}
        >
          {loadingCategories
            ? i18n.t('inventory.loadingCategories')
            : selectedLabel || (categories.length === 0 ? i18n.t('inventory.noUomCategoriesAvailable') : i18n.t('inventory.selectUnitOfMeasure'))}
        </span>
        <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.65 }} />
      </button>

      {open && (
        <div className="inv-metric-tree__panel" role="listbox">
          {categories.length === 0 ? (
            <div className="inv-metric-tree__empty"><Trans i18nKey="inventory.noMetricCategoriesFromApi" /></div>
          ) : (
            categories.map((cat) => {
              const isExpanded = !!expanded[cat];
              const children = childrenByCategory[cat] || [];
              const isLoading = loadingCategory === cat;

              return (
                <div key={cat} className="inv-metric-tree__group">
                  <button
                    type="button"
                    className="inv-metric-tree__category"
                    onClick={() => toggleCategory(cat)}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span>{cat}</span>
                    {isLoading && <Loader2 size={14} className="inv-metric-tree__spin" />}
                  </button>

                  {isExpanded && (
                    <div className="inv-metric-tree__children">
                      {!isLoading && children.length === 0 && (
                        <div className="inv-metric-tree__empty inv-metric-tree__empty--child">
                          <Trans i18nKey="inventory.noMetricsInCategory" />
                        </div>
                      )}
                      {children.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className={`inv-metric-tree__metric ${
                            String(value) === String(m.id) ? 'is-selected' : ''
                          }`}
                          onClick={() => selectMetric(cat, m)}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
          {childError && (
            <div className="inv-metric-tree__error">{childError}</div>
          )}
        </div>
      )}
    </div>
  );
}
